import { useMemo, useState } from "react";
import { AlertTriangle, Info } from "lucide-react";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";

const money = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.max(0, n));

const pct = (n) => `${n.toFixed(1)}%`;

export const Calculator = () => {
  const [capital, setCapital] = useState(5000);
  const [target, setTarget] = useState(1.5);
  const [days, setDays] = useState(20);
  const [loss, setLoss] = useState(30);

  const band = useMemo(() => {
    if (target <= 1)
      return {
        label: "Conservative: smaller cycles, smaller baskets to carry.",
        color: "var(--gm-success)",
      };
    if (target <= 2)
      return {
        label: "Moderate: the range most users settle on after testing.",
        color: "var(--gm-info)",
      };
    return {
      label: "Aggressive: bigger exposure per cycle and a real chance of a heavy drawdown.",
      color: "var(--gm-warning)",
    };
  }, [target]);

  const r = useMemo(() => {
    const c = Math.max(0, Number(capital) || 0);
    const t = target / 100;
    const d = Math.max(1, Number(days) || 1);
    const l = loss / 100;

    const growth = (months) => c * Math.pow(1 + t, d * months);

    // Recovery maths after a single bad basket.
    const afterLoss = c * (1 - l);
    const gainNeeded = l >= 1 ? Infinity : (1 / (1 - l) - 1) * 100;
    const daysToRecover =
      l >= 1 || t <= 0 ? Infinity : Math.log(1 / (1 - l)) / Math.log(1 + t);

    return {
      capital: c,
      m1: growth(1),
      m3: growth(3),
      m6: growth(6),
      m12: growth(12),
      monthPct: (Math.pow(1 + t, d) - 1) * 100,
      afterLoss,
      gainNeeded,
      daysToRecover,
      winDaysNeeded: d,
    };
  }, [capital, target, days, loss]);

  return (
    <section id="calculator" className="gm-section" style={{ background: "var(--gm-navy-950)" }}>
      <div className="gm-container" data-testid="calculator-section">
        <SectionHeader
          eyebrow="Target and risk calculator"
          title="Kira dua-dua belah, not just the upside"
          subtitle="Goldmap EA lets you set a daily profit target, and once it is hit the EA stops opening new cycles for the day. This tool shows what a target would compound to, and right next to it, what one bad basket costs you. Both numbers matter."
          testId="calculator-header"
        />

        <div className="mt-10 grid gap-6 lg:grid-cols-12 lg:gap-8">
          {/* ---------------- Inputs ---------------- */}
          <div className="lg:col-span-5">
            <div
              className="rounded-xl p-5 sm:p-6"
              style={{ background: "#0a1230", border: "1px solid rgba(255,255,255,0.1)" }}
            >
              <div>
                <Label htmlFor="calc-capital" className="text-white">
                  Starting capital (USD)
                </Label>
                <Input
                  id="calc-capital"
                  type="number"
                  min={0}
                  value={capital}
                  onChange={(e) => setCapital(e.target.value)}
                  data-testid="calc-capital-input"
                  className="mt-1.5"
                />
              </div>

              <div className="mt-6">
                <div className="flex items-baseline justify-between">
                  <Label className="text-white">Daily target</Label>
                  <span
                    className="font-mono-gm text-sm font-bold"
                    data-testid="calc-target-value"
                    style={{ color: "var(--gm-red)" }}
                  >
                    {target.toFixed(1)}%
                  </span>
                </div>
                <Slider
                  value={[target]}
                  onValueChange={(v) => setTarget(v[0])}
                  min={0.5}
                  max={4}
                  step={0.1}
                  className="mt-3"
                  data-testid="calc-target-slider"
                />
                <div className="mt-2.5 flex items-center justify-between text-[10px]" style={{ color: "var(--gm-muted-2)" }}>
                  <span>0.5%</span>
                  <span>4%</span>
                </div>
                <p
                  className="mt-2 text-[11px] font-semibold"
                  data-testid="calc-target-band"
                  style={{ color: band.color }}
                >
                  {band.label}
                </p>
                <p className="mt-1.5 text-[11px]" style={{ color: "var(--gm-muted-2)" }}>
                  This is the target you configure in the EA, not a promised result. The
                  higher you push it, the harder the EA has to work per cycle, and the
                  bigger the basket it may end up carrying.
                </p>
              </div>

              <div className="mt-6">
                <Label htmlFor="calc-days" className="text-white">
                  Trading days per month
                </Label>
                <Input
                  id="calc-days"
                  type="number"
                  min={1}
                  max={31}
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  data-testid="calc-days-input"
                  className="mt-1.5"
                />
              </div>

              <div className="mt-6">
                <div className="flex items-baseline justify-between">
                  <Label className="text-white">One bad basket costs</Label>
                  <span
                    className="font-mono-gm text-sm font-bold"
                    data-testid="calc-loss-value"
                    style={{ color: "var(--gm-warning)" }}
                  >
                    -{loss}%
                  </span>
                </div>
                <Slider
                  value={[loss]}
                  onValueChange={(v) => setLoss(v[0])}
                  min={10}
                  max={100}
                  step={5}
                  className="mt-3"
                  data-testid="calc-loss-slider"
                />
                <p className="mt-2 text-[11px]" style={{ color: "var(--gm-muted-2)" }}>
                  Basket and recovery strategies can lose a large chunk in one move.
                  Drag it to 100% to see a full account loss.
                </p>
              </div>
            </div>
          </div>

          {/* ---------------- Results ---------------- */}
          <div className="lg:col-span-7 space-y-5">
            {/* Target scenario */}
            <div
              className="rounded-xl p-5 sm:p-6"
              data-testid="calc-target-panel"
              style={{ background: "#0a1230", border: "1px solid rgba(255,255,255,0.1)" }}
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-sm font-bold text-white">
                  If every single day hit the target
                </h3>
                <span
                  className="shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider"
                  style={{
                    background: "rgba(246,196,83,0.12)",
                    border: "1px solid rgba(246,196,83,0.35)",
                    color: "var(--gm-warning)",
                  }}
                >
                  Target, not forecast
                </span>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  { k: "1 month", v: r.m1, id: "m1" },
                  { k: "3 months", v: r.m3, id: "m3" },
                  { k: "6 months", v: r.m6, id: "m6" },
                  { k: "12 months", v: r.m12, id: "m12" },
                ].map((b) => (
                  <div key={b.id} data-testid={`calc-${b.id}`}>
                    <p className="text-[11px]" style={{ color: "var(--gm-muted-2)" }}>
                      {b.k}
                    </p>
                    <p className="font-mono-gm mt-1 text-sm font-bold text-white sm:text-base">
                      {money(b.v)}
                    </p>
                  </div>
                ))}
              </div>

              <p
                className="mt-5 flex gap-2.5 rounded-xl p-3 text-[11px] leading-relaxed"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "var(--gm-muted)",
                }}
              >
                <Info
                  className="mt-0.5 h-3.5 w-3.5 shrink-0"
                  style={{ color: "var(--gm-info)" }}
                  aria-hidden="true"
                />
                <span>
                  This assumes {r.winDaysNeeded} winning days out of {r.winDaysNeeded},
                  every month, with no losing day at all. That does not happen in real
                  trading. Treat these figures as the ceiling of a target, never as
                  income you can expect.
                </span>
              </p>
            </div>

            {/* Reality check */}
            <div
              className="rounded-xl p-5 sm:p-6"
              data-testid="calc-risk-panel"
              style={{
                background: "rgba(224,31,38,0.07)",
                border: "1px solid rgba(224,31,38,0.4)",
              }}
            >
              <h3 className="flex items-center gap-2 text-sm font-bold text-white">
                <AlertTriangle
                  className="h-4 w-4"
                  style={{ color: "#FF8A8E" }}
                  aria-hidden="true"
                />
                Now the other side
              </h3>

              <div className="mt-5 grid gap-4 sm:grid-cols-3">
                <div data-testid="calc-after-loss">
                  <p className="text-[11px]" style={{ color: "var(--gm-muted-2)" }}>
                    Balance after one bad basket
                  </p>
                  <p className="font-mono-gm mt-1 text-base font-bold" style={{ color: "#FF8A8E" }}>
                    {money(r.afterLoss)}
                  </p>
                </div>
                <div data-testid="calc-gain-needed">
                  <p className="text-[11px]" style={{ color: "var(--gm-muted-2)" }}>
                    Gain needed just to break even
                  </p>
                  <p className="font-mono-gm mt-1 text-base font-bold text-white">
                    {Number.isFinite(r.gainNeeded) ? `+${pct(r.gainNeeded)}` : "Account gone"}
                  </p>
                </div>
                <div data-testid="calc-recovery-days">
                  <p className="text-[11px]" style={{ color: "var(--gm-muted-2)" }}>
                    Winning days to climb back
                  </p>
                  <p className="font-mono-gm mt-1 text-base font-bold text-white">
                    {Number.isFinite(r.daysToRecover)
                      ? `${Math.ceil(r.daysToRecover)} days`
                      : "Never"}
                  </p>
                </div>
              </div>

              <p className="mt-5 text-xs leading-relaxed" style={{ color: "var(--gm-muted)" }}>
                {loss >= 100
                  ? "At a full loss there is no recovery percentage. The account is finished and no daily target can bring it back."
                  : `A ${loss}% drawdown wipes out roughly ${Math.ceil(
                      r.daysToRecover
                    )} winning days at a ${target.toFixed(
                      1
                    )}% daily target. This is why the lot cap and equity guard in the settings matter more than the target itself.`}
              </p>
            </div>

            <p
              className="rounded-xl p-4 text-[11px] leading-relaxed"
              data-testid="calc-disclaimer"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "var(--gm-muted-2)",
              }}
            >
              This calculator is a maths tool, not a projection of earnings. Goldmap EA
              does not guarantee any return, and a daily target being available in the
              settings does not mean it will be reached. Trading gold with leverage can
              lose your entire capital. Only run money you can genuinely afford to lose.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
